import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import Link from 'next/link';
import type { ElementType, HTMLAttributes, ReactNode } from 'react';
import styles from './SectionHeader.module.scss';
export type SectionHeaderAlign = 'start' | 'center';
export type SectionHeaderAs = 'header' | 'div';
export type SectionHeaderLayout = 'stack' | 'split';
export type SectionHeaderSpacing = 'none' | 'compact' | 'default' | 'spacious';
export type SectionHeaderTitleAs = 'h1' | 'h2' | 'h3' | 'h4';
export type SectionHeaderTitleScale = 'sm' | 'md' | 'lg';
export type SectionHeaderAction = {
  ariaLabel?: string;
  href: string;
  label: ReactNode;
  onClick?: never;
} | {
  ariaLabel?: string;
  href?: never;
  label: ReactNode;
  onClick: () => void;
};
export interface SectionHeaderProps extends Omit<HTMLAttributes<HTMLElement>, 'className' | 'style' | 'title'> {
  action?: SectionHeaderAction;
  actions?: ReactNode;
  align?: SectionHeaderAlign;
  as?: SectionHeaderAs;
  children?: never;
  className?: never;
  description?: ReactNode;
  eyebrow?: ReactNode;
  layout?: SectionHeaderLayout;
  /** @deprecated Use `titleScale` instead. */
  size?: SectionHeaderTitleScale;
  spacing?: SectionHeaderSpacing;
  style?: never;
  title: ReactNode;
  titleAs?: SectionHeaderTitleAs;
  titleScale?: SectionHeaderTitleScale;
}
function SectionHeader(t0) {
  const $ = _c(43);
  let action;
  let actions;
  let description;
  let eyebrow;
  let props;
  let size;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let title;
  let titleScale;
  if ($[0] !== t0) {
    const {
      action: t6,
      actions: t7,
      align: t8,
      as: t9,
      className: _className,
      description: t10,
      eyebrow: t11,
      layout: t12,
      size: t13,
      spacing: t14,
      style: _style,
      title: t15,
      titleAs: t16,
      titleScale: t17,
      ...t18
    } = t0;
    action = t6;
    actions = t7;
    t1 = t8;
    t2 = t9;
    description = t10;
    eyebrow = t11;
    t3 = t12;
    size = t13;
    t4 = t14;
    title = t15;
    t5 = t16;
    titleScale = t17;
    props = t18;
    $[0] = t0;
    $[1] = action;
    $[2] = actions;
    $[3] = description;
    $[4] = eyebrow;
    $[5] = props;
    $[6] = size;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = title;
    $[13] = titleScale;
  } else {
    action = $[1];
    actions = $[2];
    description = $[3];
    eyebrow = $[4];
    props = $[5];
    size = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    title = $[12];
    titleScale = $[13];
  }
  const align = t1 === undefined ? "start" : t1;
  const as = t2 === undefined ? "header" : t2;
  const layout = t3 === undefined ? "split" : t3;
  const spacing = t4 === undefined ? "default" : t4;
  const titleAs = t5 === undefined ? "h2" : t5;
  const Component = as as ElementType;
  const Heading = titleAs as ElementType;
  const resolvedTitleScale = titleScale ?? size ?? "md";
  let t6;
  if ($[14] !== action || $[15] !== actions) {
    t6 = actions ?? (action ? <Action action={action} /> : null);
    $[14] = action;
    $[15] = actions;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const resolvedActions = t6;
  const t7 = styles[`helios-section-header--align-${align}`];
  const t8 = styles[`helios-section-header--layout-${layout}`];
  const t9 = styles[`helios-section-header--spacing-${spacing}`];
  let t10;
  if ($[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t10 = classNames(styles["helios-section-header"], t7, t8, t9);
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== eyebrow) {
    t11 = eyebrow ? <div className={styles["helios-section-header__eyebrow"]}>{eyebrow}</div> : null;
    $[21] = eyebrow;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const t12 = styles[`helios-section-header__title--${resolvedTitleScale}`];
  let t13;
  if ($[23] !== t12) {
    t13 = classNames(styles["helios-section-header__title"], t12);
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== Heading || $[26] !== t13 || $[27] !== title) {
    t14 = <Heading data-helios-section-header-title="" className={t13}>{title}</Heading>;
    $[25] = Heading;
    $[26] = t13;
    $[27] = title;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== description) {
    t15 = description ? <div className={styles["helios-section-header__description"]}>{description}</div> : null;
    $[29] = description;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t11 || $[32] !== t14 || $[33] !== t15) {
    t16 = <div className={styles["helios-section-header__content"]}>{t11}{t14}{t15}</div>;
    $[31] = t11;
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== resolvedActions) {
    t17 = resolvedActions ? <div className={styles["helios-section-header__actions"]}>{resolvedActions}</div> : null;
    $[35] = resolvedActions;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== Component || $[38] !== props || $[39] !== t10 || $[40] !== t16 || $[41] !== t17) {
    t18 = <Component {...props} data-helios-section-header="" className={t10}>{t16}{t17}</Component>;
    $[37] = Component;
    $[38] = props;
    $[39] = t10;
    $[40] = t16;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  return t18;
}
function Action(t0) {
  const $ = _c(12);
  const {
    action
  } = t0;
  const className = styles["helios-section-header__action"];
  if ("href" in action && action.href !== undefined) {
    let t1;
    if ($[0] !== action.label) {
      t1 = <span>{action.label}</span>;
      $[0] = action.label;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== action.ariaLabel || $[3] !== action.href || $[4] !== t1) {
      t2 = <Link href={action.href} aria-label={action.ariaLabel} className={className}>{t1}</Link>;
      $[2] = action.ariaLabel;
      $[3] = action.href;
      $[4] = t1;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t1;
  if ($[6] !== action.label) {
    t1 = <span>{action.label}</span>;
    $[6] = action.label;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== action.ariaLabel || $[9] !== action.onClick || $[10] !== t1) {
    t2 = <button type="button" aria-label={action.ariaLabel} className={className} onClick={action.onClick}>{t1}</button>;
    $[8] = action.ariaLabel;
    $[9] = action.onClick;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}
export default SectionHeader;